import { motion, AnimatePresence } from 'framer-motion';
import { useState, useRef, useEffect } from 'react';
import { MapPin, ChevronDown } from 'lucide-react';

const horaires = [
  { jour: 'Lundi', heures: 'Fermé', closed: true },
  { jour: 'Mardi', heures: '10:00 - 20:00' },
  { jour: 'Mercredi', heures: '10:00 - 20:00' },
  { jour: 'Jeudi', heures: '10:00 - 20:00' },
  { jour: 'Vendredi', heures: '10:00 - 20:00' },
  { jour: 'Samedi', heures: '10:00 - 20:00' },
  { jour: 'Dimanche', heures: '10:00 - 20:00' },
];

const DEST_ADDRESS = '65 Boulevard Westinghouse, 93270 Sevran';
const MAPS_LINK = 'https://maps.app.goo.gl/MYjhdAwtngvYopSZ9';

interface Suggestion {
  label: string;
}

const ContactSection = () => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showAllHours, setShowAllHours] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const today = new Date().getDay();
  const todayIndex = today === 0 ? 6 : today - 1;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const searchAddress = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 3) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(value)}&limit=5`
        );
        const data = await res.json();
        setSuggestions(
          data.features?.map((f: any) => ({ label: f.properties.label })) ?? []
        );
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 300);
  };

  const selectSuggestion = (s: Suggestion) => {
    setQuery(s.label);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleCalculer = () => {
    const origin = encodeURIComponent(query || 'Ma position');
    const dest = encodeURIComponent(DEST_ADDRESS);
    window.open(`https://www.google.com/maps/dir/${origin}/${dest}`, '_blank');
  };

  return (
    <section className="bg-background px-6 pt-10 pb-20 md:px-12 md:pt-10 md:pb-20 lg:px-20 xl:px-32">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 items-stretch gap-12 lg:grid-cols-2">
          {/* Left column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-8">
              <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">
                Nous trouver
              </h2>
            </div>

            <p className="mb-3 text-sm text-muted-foreground">Voir l'itinéraire depuis :</p>
            <div className="relative mb-10" ref={wrapperRef}>
              <div className="flex">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => searchAddress(e.target.value)}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  placeholder="POINT DE DÉPART"
                  className="flex-1 rounded-l-lg border border-r-0 border-border bg-card px-4 py-3.5 text-sm text-foreground placeholder:text-muted-foreground/40 placeholder:uppercase focus:outline-none focus:ring-1 focus:ring-primary/30"
                />
                <button
                  onClick={handleCalculer}
                  className="rounded-r-lg bg-primary px-3 py-3 text-xs font-bold uppercase tracking-wider text-primary-foreground transition-opacity hover:opacity-90 md:px-8 md:py-3.5 md:text-sm md:tracking-widest"
                >
                  Calculer
                </button>
              </div>

              {showSuggestions && suggestions.length > 0 && (
                <ul className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-lg border border-border bg-card shadow-xl">
                  {suggestions.map((s, i) => (
                    <li key={i}>
                      <button
                        onClick={() => selectSuggestion(s)}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-secondary/50"
                      >
                        <MapPin size={14} className="shrink-0 text-muted-foreground" />
                        {s.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <h3 className="mb-5 font-heading text-xl font-bold text-foreground">
              Nos horaires
            </h3>

            {/* Desktop */}
            <div className="hidden md:block">
              {horaires.map((h) => (
                <div
                  key={h.jour}
                  className={`flex items-center justify-between px-4 py-3.5 ${
                    h.closed
                      ? 'mt-1 rounded-lg bg-primary/10'
                      : 'border-b border-border/50'
                  }`}
                >
                  <span className={`text-sm ${h.closed ? 'font-semibold text-primary' : 'text-foreground'}`}>
                    {h.jour}
                  </span>
                  <span className={`text-sm font-medium ${h.closed ? 'font-semibold text-primary' : 'text-foreground'}`}>
                    {h.heures}
                  </span>
                </div>
              ))}
            </div>

            {/* Mobile */}
            <div className="md:hidden">
              <div
                className={`flex items-center justify-between px-4 py-3.5 ${
                  horaires[todayIndex]?.closed
                    ? 'rounded-lg bg-primary/10'
                    : 'border-b border-border/50'
                }`}
              >
                <span className={`text-sm font-semibold ${horaires[todayIndex]?.closed ? 'text-primary' : 'text-foreground'}`}>
                  {horaires[todayIndex]?.jour} (aujourd'hui)
                </span>
                <span className={`text-sm font-medium ${horaires[todayIndex]?.closed ? 'text-primary' : 'text-foreground'}`}>
                  {horaires[todayIndex]?.heures}
                </span>
              </div>

              <button
                onClick={() => setShowAllHours(!showAllHours)}
                className="mt-3 mb-2 flex items-center gap-1 text-sm text-accent hover:underline"
              >
                {showAllHours ? 'Masquer' : 'Voir plus'}
                <motion.span animate={{ rotate: showAllHours ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <ChevronDown size={16} />
                </motion.span>
              </button>

              <AnimatePresence>
                {showAllHours && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    {horaires.map((h, i) => {
                      if (i === todayIndex) return null;
                      return (
                        <div
                          key={h.jour}
                          className={`flex items-center justify-between px-4 py-3.5 ${
                            h.closed
                              ? 'mt-1 rounded-lg bg-primary/10'
                              : 'border-b border-border/50'
                          }`}
                        >
                          <span className={`text-sm ${h.closed ? 'font-semibold text-primary' : 'text-foreground'}`}>
                            {h.jour}
                          </span>
                          <span className={`text-sm font-medium ${h.closed ? 'font-semibold text-primary' : 'text-foreground'}`}>
                            {h.heures}
                          </span>
                        </div>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Right column: Map */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="overflow-hidden rounded-2xl shadow-xl lg:self-stretch"
          >
            <iframe
              src="https://maps.google.com/maps?q=Body+By+CC+Institut+Sant%C3%A9+Esth%C3%A9tique+65+Bd+Westinghouse+93270+Sevran&output=embed&hl=fr"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Body by CC - Institut Santé & Esthétique - 65 Boulevard Westinghouse, 93270 Sevran"
              className="h-full min-h-[400px] w-full"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
